import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Slide {
  slide_number: number;
  type: 'title' | 'content' | 'two_column' | 'quote';
  title: string;
}

interface SourcesStepProps {
  documents: { id: string; title: string }[];
  selectedDocs: string[];
  toggleDoc: (id: string) => void;
  onBack: () => void;
  onNext: () => void;
}

export function SourcesStep({
  documents,
  selectedDocs,
  toggleDoc,
  onBack,
  onNext,
}: SourcesStepProps) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Optionally select documents to use as source material.
      </p>
      <div className="max-h-60 space-y-1 overflow-y-auto rounded border p-2">
        {documents.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No documents available
          </p>
        ) : (
          documents.map((doc) => (
            <button
              key={doc.id}
              onClick={() => toggleDoc(doc.id)}
              className={`flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm transition-colors ${
                selectedDocs.includes(doc.id)
                  ? 'bg-primary/10 text-primary'
                  : 'hover:bg-accent'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedDocs.includes(doc.id)}
                readOnly
                className="pointer-events-none"
              />
              <span className="truncate">{doc.title}</span>
            </button>
          ))
        )}
      </div>
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>Back</Button>
        <Button onClick={onNext}>Generate</Button>
      </div>
    </div>
  );
}

interface ReviewStepProps {
  isPending: boolean;
  isError: boolean;
  slides: Slide[] | null;
  onRetry: () => void;
  onBack: () => void;
  onDone: () => void;
}

export function ReviewStep({
  isPending,
  isError,
  slides,
  onRetry,
  onBack,
  onDone,
}: ReviewStepProps) {
  return (
    <div className="space-y-4">
      {isPending ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="mt-3 text-sm text-muted-foreground">Generating slides...</p>
        </div>
      ) : isError ? (
        <div className="py-8 text-center">
          <p className="text-sm text-destructive">Failed to generate. Please try again.</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>Retry</Button>
        </div>
      ) : slides ? (
        <div className="space-y-3">
          <p className="text-sm font-medium">Generated {slides.length} slides:</p>
          <div className="max-h-60 space-y-2 overflow-y-auto">
            {slides.map((slide) => (
              <div
                key={slide.slide_number}
                className="flex items-center gap-3 rounded border px-3 py-2 text-sm"
              >
                <span className="shrink-0 font-mono text-xs text-muted-foreground">
                  #{slide.slide_number}
                </span>
                <span className="truncate font-medium">{slide.title}</span>
                <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                  {slide.type}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} disabled={isPending}>Back</Button>
        <Button onClick={onDone} disabled={isPending}>Done</Button>
      </div>
    </div>
  );
}
